import { useState, useEffect } from "react";
import { KColorInputProps } from "./types";
import { FormattedMessage } from "react-intl";
import Button from "@ui-components/Button";
import { KColorProductSchema, ProductInput } from "./product-input";
import { ColorInput, ColorSchema } from "./color-input";
import { PackageInput } from "./package-input/component";
import { SimpleProductSchema } from "./package-input";
import _ from "lodash";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";



export const KColorInput = ({
  value,
  onChange,
  selectableProducts,
  selectableColorsExceptions,
  inSurvey=false,
  disabled=false,
}: KColorInputProps) => {
  const [startResearchFrom, setStartResearchFrom] = useState<"product" | "color">("product");
  const [initialLoad, setInitialLoad] = useState(true);
  const [kColorProduct, setKColorProduct] = useState<KColorProductSchema | undefined>(value?.kColorProduct);
  const [color, setColor] = useState<ColorSchema | undefined>(value?.color);
  const [productPackage, setProductPackage] = useState<number | undefined>(value?.productPackage);
  const [productCompatibleColors, setProductCompatibleColors] = useState<string[]>();

  const countryApiRequest = useCountryAxiosInstance();

  useEffect(() => {
    // Reset all fields if value is cleared
    if (!!value) return;
    setKColorProduct(undefined);
    setColor(undefined);
    setProductPackage(undefined);
  }, [value]);

  useEffect(() => {
    // When the initial value changes, update the internal state
    setKColorProduct(initialLoad? value?.kColorProduct : undefined);
    setColor(initialLoad? value?.color : undefined);
    setProductPackage(initialLoad? value?.productPackage : undefined);
    setInitialLoad(false);
  }, [startResearchFrom]);

  useEffect(() => {
    // Notify parent component of changes
    onChange({
      kColorProduct,
      productPackage,
      color,
      edit_id: value?.edit_id,
    });
  }, [kColorProduct, color, productPackage]);


  useEffect(() => {
    // If a product is provided, we need to fetch its info to get the compatible colors
    if (!kColorProduct) {
      setProductCompatibleColors(undefined);
      setProductPackage(undefined);
      if (startResearchFrom === "product") {
        setColor(undefined);
      }
    } else {
      countryApiRequest!
        .get(`/kcolors/products/${kColorProduct.id}`, {publicRequest: true})
        .then((res: any) => {
          setProductCompatibleColors(res.lst_available_colors);
          if (color && !res.lst_available_colors.includes(color.code)) {
            setColor(undefined);
          }
        });
    }
  }, [kColorProduct]);

  return (
    //@ts-ignore
    <div className="flex flex-col items-start justify-center w-full gap-5">
      {/* ----- First element selection (product or color). Only available in pricebook context, not in surveys */}
      {!inSurvey && <>
        <p className="font-bold">
          <FormattedMessage
            id="start_research_from"
            defaultMessage={"Comincia la ricerca da:"}
          />
        </p>
        <div className="grid grid-cols-2 items-start justify-center gap-2">
          <Button
            onClick={() => {
              if (!!disabled) return;
              setStartResearchFrom("product");
            }}
            styleType={startResearchFrom === "product" ? "default" : "empty"}
            className="w-full"
            disabled={disabled}
          >
            <FormattedMessage id="product" defaultMessage={"Prodotto"}/>
          </Button>
          <Button
            onClick={() => setStartResearchFrom("color")}
            styleType={startResearchFrom === "color" ? "default" : "empty"}
            className="w-full"
          >
            <FormattedMessage id="color" defaultMessage={"Colore"}/>
          </Button>
        </div>
        <hr className="w-full border-gray-300"/>
      </>}
      <div className="flex flex-col gap-5 w-full">
        {/* ----- Product selector */}
        {(startResearchFrom === "product" || !!color) && (
          <div className={startResearchFrom === "product" ? "order-first" : "order-last"}>
            <ProductInput
              kColorProduct={kColorProduct}
              setKColorProduct={setKColorProduct}
              selectableProducts={
              selectableProducts ?
                Object.keys(selectableProducts ?? {}).map(key => ({
                  value: key,
                  label: key,
                })): "__all__"
              }
              color={color}
              inSurvey={inSurvey}
              disabled={disabled}
              startResearchFrom={startResearchFrom}
              productCompatibleColors={productCompatibleColors}
            />
          </div>
        )}
        {(!!color || !!kColorProduct) && (
          <hr className="w-full border-gray-300"/>
        )}
        {/* ----- Color selector */}
        {(startResearchFrom === "color" || !!kColorProduct) && (
          <div className={startResearchFrom === "color" ? "order-first" : "order-last"}>
            <ColorInput
              color={color}
              setColor={setColor}
              kColorProduct={kColorProduct}
              unselectableColors={_.uniq([
                ...(!!kColorProduct
                  ? (
                    selectableColorsExceptions
                      ?.find(({product: {kColorProduct: {cod_prod_comm: c}}}) => c === kColorProduct.cod_prod_comm)
                      ?.colors
                    ?? []
                  ).map((c => c.value))
                  : []
                ),
              ])}
              disabled={disabled}
              productCompatibleColors={productCompatibleColors}
              startResearchFrom={startResearchFrom}
            />
          </div>
        )}
      </div>
      {/* ----- Package selector */}
      {!!color && !!kColorProduct && <>
        <hr className="w-full border-gray-300" />
        <PackageInput
          kColorProduct={kColorProduct}
          selectableOptions={selectableProducts ? selectableProducts[kColorProduct.cod_prod_comm] ?? []: "__all__"}
          productPackage={productPackage}
          setProductPackage={setProductPackage}
          disabled={disabled}
          color={color}
        />
      </>}
    </div>
  );
};
